import { SerializedStyles } from "@emotion/react";
import { ReactNode, Ref, memo, useCallback } from "react";
import { FormFlowPage, Skeleton } from "src/components";
import { UnverifiedId, VerifiedId } from "src/icons";
import { noInput } from "src/utilities/input";

import { enums } from "@fraction/shared";

export interface IdVerificationDecisionLayoutProps {
  value?: enums.IdVerificationDecision;
  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
  children?: ReactNode;
  nextLoading?: boolean;
}

const getTitle = (decision?: enums.IdVerificationDecision) => {
  if (!decision) {
    return <Skeleton height={40} width={400} />;
  }
  return decision === enums.IdVerificationDecision.PASS
    ? "Thank you for verifying your identity"
    : "Unable to verify your identity";
};

const getDescription = (decision?: enums.IdVerificationDecision) => {
  if (!decision) {
    return <Skeleton height={88} width={600} />;
  }
  return decision === enums.IdVerificationDecision.PASS
    ? "You may continue filling out the application."
    : "Unfortunately, your answers didn’t match the bureau’s records, and we couldn’t verify your identity. " +
        "You may continue filling out the application, but note that we’ll follow up with additional " +
        "identity verification steps following submission.";
};

const getIcon = (decision?: enums.IdVerificationDecision) => {
  if (!decision) {
    return <Skeleton height={150} width={200} />;
  }
  return decision === enums.IdVerificationDecision.PASS ? (
    <VerifiedId height="100%" width={175} />
  ) : (
    <UnverifiedId height="100%" width={200} />
  );
};

const IdVerificationDecision = ({
  value,
  focusRef,
  onNext,
  onBack,
  style,
  children,
  nextLoading,
  ...props
}: IdVerificationDecisionLayoutProps) => {
  const handleOnNext = useCallback(
    (evt: React.ChangeEvent<HTMLFormElement>) => {
      evt?.preventDefault();
      onNext?.();
    },
    [onNext]
  );

  return (
    <FormFlowPage
      {...props}
      ref={focusRef}
      icon={getIcon(value)}
      title={getTitle(value)}
      description={getDescription(value)}
      onNext={handleOnNext}
      onBack={onBack}
      style={style}
      nextDisabled={noInput(value)}
      nextLoading={nextLoading}
    >
      {children}
    </FormFlowPage>
  );
};

export default memo(IdVerificationDecision);
